import React, { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import toast from 'react-hot-toast';
import {
  useCreateAdminCompanyPhoneNumberApi,
  useDeleteAdminCompanyPhoneNumberApi,
  useGetAdminCompanyApi,
  useGetAdminCompanyPhoneNumbersApi,
  useGetHostedNumberOrdersApi,
  useUpdateAdminCompanyPhoneNumberApi,
} from '../api';
import { CompanyPhoneType } from '../api/CompanyPhoneApi';
import { calculateStatusAndDescription } from '../helpers';
import {
  CompanyPhoneFormRow,
  CompanyPhonesTabProps,
  CompanyPhonesTabViewModel,
  HostedNumberOrderResponse,
} from '../types';

type CompanyPhoneWithType = {
  id: string;
  number: string;
  type?: CompanyPhoneType;
};

export const useCompanyPhonesTabController = (
  params: CompanyPhonesTabProps,
): CompanyPhonesTabViewModel => {
  const { company, refreshCompany } = params;

  const [phoneNumbers, setPhoneNumbers] = useState<CompanyPhoneFormRow[]>(
    company.phoneNumbers.map(p => ({ ...p, type: (p as CompanyPhoneWithType).type || undefined })),
  );

  const [phoneErrors, setPhoneErrors] = useState<{ [key: string]: string }>({});
  const [setupHostedNumberDialogOpen, setSetupHostedNumberDialogOpen] = useState(false);
  const [setupHostedNumberDialog, setSetupHostedNumberDialog] = useState<{ id: string; number: string; orderId?: string } | null>(null);
  const [hostedNumberOrders, setHostedNumberOrders] = useState<HostedNumberOrderResponse[]>(company.additionalData?.telnyx?.hostedNumberOrders ?? []);

  const getCompany = useGetAdminCompanyApi();
  const getAdminCompanyPhoneNumbers = useGetAdminCompanyPhoneNumbersApi();
  const createAdminCompanyPhoneNumbers = useCreateAdminCompanyPhoneNumberApi();
  const deleteAdminCompanyPhoneNumbers = useDeleteAdminCompanyPhoneNumberApi();
  const updateAdminCompanyPhoneNumbers = useUpdateAdminCompanyPhoneNumberApi();
  const getHostedNumberOrders = useGetHostedNumberOrdersApi();

  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const modifiedPhoneNumbers = phoneNumbers.map((p) => {
      if (p.id === name) {
        p.number = value;
      }
      return p;
    });
    setPhoneNumbers(modifiedPhoneNumbers);
  };

  const onPhoneNumberTypeChange = (event: SelectChangeEvent<string>, phoneId: string) => {
    const value = event.target.value === '' ? undefined : event.target.value as CompanyPhoneType;
    const modifiedPhoneNumbers = phoneNumbers.map((p) => {
      if (p.id === phoneId) {
        p.type = value;
      }
      return p;
    });
    setPhoneNumbers(modifiedPhoneNumbers);

    // Clear error when type is selected
    if (value) {
      setPhoneErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[phoneId];
        return newErrors;
      });
    }
  };

  const onPhoneNumberDelete = (id: string) => {
    const modifiedPhoneNumbers = phoneNumbers.map((p) => {
      if (p.id === id) {
        p.isDeleted = true;
      }
      return p;
    });
    setPhoneNumbers(modifiedPhoneNumbers);
  };

  const onAddNewPhoneNumber = () => {
    const newPhoneNumbers = phoneNumbers.concat([
      {
        id: Date.now().toString(36) + Math.random().toString(36).substring(2),
        number: "",
        type: undefined,
        isDeleted: false,
        isNew: true,
      },
    ]);
    setPhoneNumbers(newPhoneNumbers);
  };

  const fetchHostedNumberOrders = async () => {
    try {
      const response = await getHostedNumberOrders(company.id);
      if (hostedNumberOrders.length === 0) {
        setHostedNumberOrders(response.data);
      } else {
        // Update hosted number orders with new data
        const updatedOrders = response.data.map(order => {
          const existingOrder = hostedNumberOrders.find(o => o.orderId === order.orderId);
          if (!existingOrder) {
            return order;
          } else {
            if (order.status !== 'pending') {
              existingOrder.detail = order.detail;
              existingOrder.status = order.status;
            }
            existingOrder.phoneNumbers.forEach(phoneNumber => {
              const receivedPhoneNumber = order.phoneNumbers.find(p => p.phoneNumber === phoneNumber.phoneNumber);
              if (receivedPhoneNumber && receivedPhoneNumber.status !== 'pending') {
                phoneNumber.detail = receivedPhoneNumber.detail;
                phoneNumber.status = receivedPhoneNumber.status;
              }
            });
          }
          return existingOrder;
        });
        const sameOrders = hostedNumberOrders.filter(o => !updatedOrders.some(h => h.orderId === o.orderId));
        setHostedNumberOrders([...updatedOrders, ...sameOrders]);
      }
    } catch (error) {
      console.error('Failed to fetch hosted number orders:', error);
    }
  };

  const fetchCompanyDataAndHostedNumberOrders = async () => {
    await getCompany(company.id).then((response) => {
      setHostedNumberOrders(response.data.additionalData?.telnyx?.hostedNumberOrders ?? []);
    });
    if (hostedNumberOrders.length === 0) {
      await fetchHostedNumberOrders();
    }
  };

  const fetchAdminCompanyPhoneNumbers = () => {
    getAdminCompanyPhoneNumbers(company.id).then((response) => {
      const phoneNumbers =
        response.data && response.data.length > 0
          ? response.data
          : [
              {
                id: Date.now().toString(36) + Math.random().toString(36).substring(2),
                number: "",
                type: undefined,
                isDeleted: false,
                isNew: true,
              },
            ];
      setPhoneNumbers(phoneNumbers);
      if (hostedNumberOrders.length === 0) {
        fetchHostedNumberOrders();
      }
    });
  };

  const onSave = () => {
    // Validate that all phone numbers have a type specified
    const phonesWithoutType = phoneNumbers.filter((p) => !p.isDeleted && p.number !== "" && p.number !== "+1" && !p.type);

    // Set inline errors for phones without type
    const newErrors: { [key: string]: string } = {};
    phonesWithoutType.forEach((p) => {
      newErrors[p.id] = "Type is required";
    });
    setPhoneErrors(newErrors);

    if (phonesWithoutType.length > 0) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: any = [];
    phoneNumbers.filter((p) => p.number !== "" && p.number !== "+1").forEach((p) => {
      if (p.isDeleted) {
        if (!p.isNew) {
          promises.push(deleteAdminCompanyPhoneNumbers(company.id, p.id));
        }
      } else if (p.isNew && !p.isDeleted) {
        promises.push(
          createAdminCompanyPhoneNumbers(company.id, p.number?.startsWith("+1") ? p.number : "+1" + p.number, p.type!)
        );
      } else if (p.id && !p.isNew && !p.isDeleted) {
        promises.push(
          updateAdminCompanyPhoneNumbers(company.id, p.id, p.number?.startsWith("+1") ? p.number : "+1" + p.number, p.type!)
        );
      }
    });
    Promise.all(promises).then(() => {
      toast.success("phones are updated");
      setPhoneErrors({}); // Clear all errors on successful save
      fetchAdminCompanyPhoneNumbers();
      refreshCompany();
    });
  };

  const getOrderForPhoneNumber = (phoneNumber: string): HostedNumberOrderResponse | undefined => {
    if (!phoneNumber) return undefined;
    return hostedNumberOrders.find(order => order.status !== 'deleted' && order.phoneNumbers.some(pn => pn.phoneNumber === phoneNumber));
  };

  const getPhoneOrderState = (phoneNumber: string) => {
    return calculateStatusAndDescription(getOrderForPhoneNumber(phoneNumber || ''), phoneNumber);
  };

  const onOpenSetupHostedNumberDialog = (phone: { id: string; number: string }) => {
    const order = getOrderForPhoneNumber(phone.number || '');
    setSetupHostedNumberDialog({
      id: phone.id,
      number: phone.number || '',
      orderId: order?.orderId || '',
    });
    setSetupHostedNumberDialogOpen(true);
  };

  const onCloseSetupHostedNumberDialog = () => {
    setSetupHostedNumberDialogOpen(false);
    fetchCompanyDataAndHostedNumberOrders();
  };

  useEffect(() => {
    fetchAdminCompanyPhoneNumbers();
  }, []);

  return {
    companyId: company.id,
    phoneNumbers,
    phoneErrors,
    setupHostedNumberDialogOpen,
    setupHostedNumberPhone: setupHostedNumberDialog,
    setupHostedNumberExistingOrder: setupHostedNumberDialog
      ? getOrderForPhoneNumber(setupHostedNumberDialog.number)
      : undefined,
    getPhoneOrderState,
    onPhoneNumberChange,
    onPhoneNumberTypeChange,
    onPhoneNumberDelete,
    onAddNewPhoneNumber,
    onOpenSetupHostedNumberDialog,
    onCloseSetupHostedNumberDialog,
    onSave,
  };
};
